import Storage from '../utils/storage';
import { LoginResponse } from '../types';

const JWT_TOKEN_KEY = 'vida_jwt_token';
const REFRESH_TOKEN_KEY = 'vida_refresh_token';
const TOKEN_EXPIRES_KEY = 'vida_token_expires';
const LOGIN_RESPONSE_KEY = 'vida_login_response';
const NEXT_URL_KEY = 'vida_next_url';
const REDIRECT_HISTORY_KEY = 'vida_redirect_history';
// Convenience for VRN's banner. Authoritative signal is the JWT's mint_source claim.
const IMPERSONATION_TARGET_UUID_KEY = 'vida_impersonation_target_uuid';
const IMPERSONATION_ID_KEY = 'vida_impersonation_id';

const REDIRECT_LOOP_WINDOW_MS = 10_000;
const REDIRECT_LOOP_MIN_HITS = 2;
const REDIRECT_HISTORY_MAX = 5;

type RedirectEntry = { url: string; ts: number };

export const storeToken = async (token: string): Promise<void> => {
  try {
    await Storage.setItem(JWT_TOKEN_KEY, token);
  } catch (error) {
    console.error('Error storing token:', error);
    throw new Error('Failed to store authentication token');
  }
};

export const storeRefreshToken = async (refreshToken: string): Promise<void> => {
  try {
    await Storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (error) {
    console.error('Error storing refresh token:', error);
    throw new Error('Failed to store refresh token');
  }
};

export const storeTokenExpires = async (expires: string): Promise<void> => {
  try {
    await Storage.setItem(TOKEN_EXPIRES_KEY, expires);
  } catch (error) {
    console.error('Error storing token expiration:', error);
  }
};

export const getToken = async (): Promise<string | null> => {
  try {
    return await Storage.getItem(JWT_TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving token:', error);
    return null;
  }
};

export const getRefreshToken = async (): Promise<string | null> => {
  try {
    return await Storage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

export const getTokenExpires = async (): Promise<string | null> => {
  try {
    return await Storage.getItem(TOKEN_EXPIRES_KEY);
  } catch (error) {
    console.error('Error retrieving token expiration:', error);
    return null;
  }
};

export const storeNextUrl = async (url: string): Promise<void> => {
  await Storage.setItem(NEXT_URL_KEY, url);
};

export const getNextUrl = async (): Promise<string | null> => {
  return Storage.getItem(NEXT_URL_KEY);
};

export const removeNextUrl = async (): Promise<void> => {
  await Storage.removeItem(NEXT_URL_KEY);
};

async function readRedirectHistory(): Promise<RedirectEntry[]> {
  try {
    const raw = await Storage.getItem(REDIRECT_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const isRedirectLoop = async (url: string): Promise<boolean> => {
  const history = await readRedirectHistory();
  const now = Date.now();
  const hits = history.filter(
    (e) => e.url === url && now - e.ts < REDIRECT_LOOP_WINDOW_MS
  ).length;
  return hits >= REDIRECT_LOOP_MIN_HITS;
};

export const recordRedirect = async (url: string): Promise<void> => {
  try {
    const history = await readRedirectHistory();
    history.push({ url, ts: Date.now() });
    const trimmed = history.slice(-REDIRECT_HISTORY_MAX);
    await Storage.setItem(REDIRECT_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // Best-effort: loop tracking is defense-in-depth. A storage failure here
    // must never block the redirect.
  }
};

export const storeLoginResponse = async (loginResponse: LoginResponse): Promise<void> => {
  try {
    await Storage.setItem(LOGIN_RESPONSE_KEY, JSON.stringify(loginResponse));
  } catch (error) {
    console.error('Error storing login response:', error);
    throw new Error('Failed to store login response');
  }
};

export const removeToken = async (): Promise<void> => {
  try {
    await Storage.removeItem(JWT_TOKEN_KEY);
    await Storage.removeItem(REFRESH_TOKEN_KEY);
    await Storage.removeItem(TOKEN_EXPIRES_KEY);
    await Storage.removeItem(LOGIN_RESPONSE_KEY);
    await Storage.removeItem(NEXT_URL_KEY);
    await Storage.removeItem(REDIRECT_HISTORY_KEY);
    await Storage.removeItem(IMPERSONATION_TARGET_UUID_KEY);
    await Storage.removeItem(IMPERSONATION_ID_KEY);
  } catch (error) {
    console.error('Error removing tokens:', error);
  }
};

export const storeImpersonationContext = async (
  targetUuid: string,
  impersonationId: string
): Promise<void> => {
  try {
    await Storage.setItem(IMPERSONATION_TARGET_UUID_KEY, targetUuid);
    await Storage.setItem(IMPERSONATION_ID_KEY, impersonationId);
    // Explicit blank — impersonation has no refresh token.
    await Storage.setItem(REFRESH_TOKEN_KEY, '');
  } catch (error) {
    console.error('Error storing impersonation context:', error);
    throw new Error('Failed to store impersonation context');
  }
};
